"use client"

import Link from "next/link";
import {useMemo} from "react";
import {usePathname} from "next/navigation";
import {SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem} from "@/components/ui/sidebar";
import {dashboardPath, dashboardsPath} from "@/app/paths";
import {useDashboards} from "@/hooks/queries/use-dashboards";
import {DashboardIcon} from "@/app/components/dashboards/DashboardA2UIPresentation";
import {compareDashboardPriority, dashboardIcon, dashboardId, dashboardTitle} from "@/lib/dashboards/presentation";

export function DashboardSidebarNav({companyId}: {companyId: number}) {
  const pathname = usePathname();
  const {dashboards, loading, error} = useDashboards(companyId, "", false);
  const prioritized = useMemo(() => [...dashboards].sort(compareDashboardPriority), [dashboards]);
  const visible = prioritized.slice(0, 3);
  const titleCounts = useMemo(() => prioritized.reduce<Record<string, number>>((counts, dashboard) => {
    const title = dashboardTitle(dashboard);
    counts[title] = (counts[title] ?? 0) + 1;
    return counts;
  }, {}), [prioritized]);

  if (!loading && !error && visible.length === 0) return null;

  return (
    <SidebarMenuSub className="group-data-[collapsible=icon]:hidden">
      {visible.map(dashboard => {
        const id = dashboardId(dashboard);
        const title = dashboardTitle(dashboard);
        const displayTitle = titleCounts[title] > 1
          ? `${title} · ${dashboard.head?.createdBy || "Unknown"} · ${id.slice(0, 8)}`
          : title;
        const href = dashboardPath(companyId, id);
        const icon = dashboardIcon(dashboard);
        return (
          <SidebarMenuSubItem key={id}>
            <DashboardIcon
              name={icon}
              className="pointer-events-none absolute -left-3 top-1/2 z-10 size-5 -translate-y-1/2 rounded-md bg-transparent text-muted-foreground shadow-none"
            />
            <SidebarMenuSubButton
              asChild
              isActive={pathname === href}
              className="text-[13px] font-medium"
            >
              <Link href={href}>
                <span className="truncate" title={displayTitle}>{displayTitle}</span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
      {loading && visible.length === 0 && (
        <SidebarMenuSubItem>
          <span className="block px-2 py-1 text-xs text-muted-foreground">Loading dashboards…</span>
        </SidebarMenuSubItem>
      )}
      {error && (
        <SidebarMenuSubItem>
          <span className="block px-2 py-1 text-xs text-destructive">
            {visible.length > 0 ? "Some dashboards unavailable" : "Dashboards unavailable"}
          </span>
        </SidebarMenuSubItem>
      )}
      {prioritized.length > 3 && (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton
            asChild
            size="sm"
            className="text-muted-foreground"
          >
            <Link href={dashboardsPath(companyId)}>
              <span className="truncate">Show all</span>
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      )}
    </SidebarMenuSub>
  );
}
